import { useEffect } from 'react'

import {
  SalaryAdvanceSettingsPageBankAccountDocument,
  SalaryAdvanceSettingsPageDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import CapPercentageSetting from 'modules/salary_advance/pages/settings/Content/CapPercentageSetting'
import ClosureDaySetting from 'modules/salary_advance/pages/settings/Content/ClosureDaySetting'
import PaymentSettings from 'modules/salary_advance/pages/settings/Content/PaymentSettings'
import ServiceEnablementSetting from 'modules/salary_advance/pages/settings/Content/ServiceEnablementSetting'
import AccrualModeSetting from 'modules/salary_advance/pages/settings/Content/AccrualModeSetting'
import {
  ACCRUAL_MODE_CALENDAR_ONLY,
} from 'modules/salary_advance/lib/companySettings'

const SalaryAdvanceSettingsPage = () => {
  const { data, loading, error } = useQuery(SalaryAdvanceSettingsPageDocument)

  const companySetting = data?.salaryAdvance?.companySettingsConnection?.nodes?.[0]
  const canManageSalaryAdvance = data?.salaryAdvance?._policies?.manageSalaryAdvance?.can ?? false
  const canManagePayments =
    data?.finance?._policies?.sidebarSeeFinancePaymentsSubLevelItem?.can ?? false
  const canView = canManageSalaryAdvance || canManagePayments
  // non-null value is expected from the backend
  const advanceCapMonthlyWagePercent = companySetting?.advanceCapMonthlyWagePercent
  // is required and always has a value
  const requestDeadlineDay = companySetting?.requestDeadlineDay
  // service enablement toggle
  const serviceEnabled = companySetting?.serviceEnabled ?? false
  const automaticPaymentEnabled = companySetting?.automaticPaymentEnabled ?? false
  const bankingBankAccountId = companySetting?.bankingBankAccountId ?? null
  const accrualMode = companySetting?.accrualMode ?? ACCRUAL_MODE_CALENDAR_ONLY
  const timeTrackingAvailableForAccrualMode =
    companySetting?.timeTrackingAvailableForAccrualMode ?? false

  const bankAccountIdAsInt = bankingBankAccountId ? parseInt(bankingBankAccountId, 10) : null
  const { data: bankAccountData } = useQuery(SalaryAdvanceSettingsPageBankAccountDocument, {
    variables: { id: bankAccountIdAsInt ?? 0 },
    skip: bankAccountIdAsInt === null,
  })
  const bankAccount = bankAccountData?.banking?.bankAccount ?? null

  useEffect(() => {
    if (!canView) return

    track(trackingEvents.salaryAdvance.viewSettings)
  }, [canView])

  if (!companySetting || requestDeadlineDay == null || advanceCapMonthlyWagePercent == null) {
    return (
      <ResponsivePage.Body loading={loading} error={error} authorized={canView}>
        {null}
      </ResponsivePage.Body>
    )
  }

  return (
    <ResponsivePage.Body authorized={canView}>
      <ServiceEnablementSetting
        serviceEnabled={serviceEnabled}
        requestDeadlineDay={requestDeadlineDay}
        advanceCapMonthlyWagePercent={advanceCapMonthlyWagePercent}
        canEdit={canManageSalaryAdvance}
      />
      <CapPercentageSetting
        currentCapPercentage={advanceCapMonthlyWagePercent}
        requestDeadlineDay={requestDeadlineDay}
        serviceEnabled={serviceEnabled}
        canEdit={canManageSalaryAdvance}
      />
      <ClosureDaySetting
        requestDeadlineDay={requestDeadlineDay}
        serviceEnabled={serviceEnabled}
        advanceCapMonthlyWagePercent={advanceCapMonthlyWagePercent}
        canEdit={canManageSalaryAdvance}
      />
      <AccrualModeSetting
        accrualMode={accrualMode}
        timeTrackingAvailableForAccrualMode={timeTrackingAvailableForAccrualMode}
        canEdit={canManageSalaryAdvance}
      />
      <PaymentSettings
        automaticPaymentEnabled={automaticPaymentEnabled}
        bankAccount={bankAccount}
        canEdit={canManagePayments}
      />
    </ResponsivePage.Body>
  )
}

export default SalaryAdvanceSettingsPage
